import React from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Award, Heart, Users, ArrowRight, Briefcase } from 'lucide-react';
import StatsSection from '../components/StatsSection';
import CtaSection from '../components/CtaSection';
import Lottie from "lottie-react";
import Team from "../assets/lottie/team-lottie.json";
import Company from "../assets/lottie/company-lottie.json";
import Office from "../assets/lottie/office-lottie.json";
import Partner from "../assets/lottie/partner-lottie.json";
import ResultDriven from "../assets/lottie/result-driven.json";
import Knowledge from "../assets/lottie/knowledge.json";
import Aditya from "../assets/team/aditya.jpeg";
import Mayank from "../assets/team/Mayank.jpeg";
import Monu from "../assets/team/Monu.jpeg";
import Sourabh from "../assets/team/sourabh.jpeg";

const AboutPage: React.FC = () => {
  // Sample stats data
  const stats = [
    {
      value: '10+',
      label: 'Years of Experience'
    },
    {
      value: '200+',
      label: 'Projects Delivered'
    },
    {
      value: '50+',
      label: 'Expert Team Members'
    },
    {
      value: '98%',
      label: 'Client Satisfaction'
    }
  ];


  // Sample team members data
  const teamMembers = [
    {
      name: 'Mayank Gupta',
      position: 'CEO & Co-Founder',
      bio: 'Mayank drives the company\'s vision and strategy, combining business acumen with technical expertise to deliver innovative solutions that transform businesses.',
      imageUrl: Mayank
    },
    {
      name: 'Aditya Parsai',
      position: 'CTO & Co-Founder',
      bio: 'A seasoned full-stack engineer, Aditya leads our technical architecture and development efforts, ensuring we deliver scalable and cutting-edge solutions.',
      imageUrl: Aditya
    },
    {
      name: 'Saurabh Arora',
      position: 'Partner & Advisor',
      bio: 'Saurabh provides strategic guidance and advisory services, leveraging his extensive industry experience to help shape our business direction and client partnerships.',
      imageUrl: Sourabh
    },
    {
      name: 'Monu Agrawal',
      position: 'Technical Advisor',
      bio: 'Monu brings deep technical expertise and industry insights, advising on architecture decisions and best practices to ensure robust and scalable solutions.',
      imageUrl: Monu
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-900 to-indigo-600 overflow-x-hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-12">
              <span className="inline-block px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium mb-8 backdrop-blur-sm">
                About Us
              </span>
              <h1 className="text-inter text-2xl md:text-4xl font-bold mb-6 text-white md:leading-[1.2]">
                We Are Passionate About Technology & Innovation
              </h1>
              <p className="text-justify text-inter md:text-xl text-blue-100 mb-8">
                At Techno-Stack-Labs, we combine technical expertise with industry knowledge to deliver cutting-edge solutions that drive business growth and innovation.
              </p>
              <Link
                to="/contact"
                className="text-inter inline-flex items-center px-6 py-3 rounded-lg bg-white text-blue-600 font-medium hover:bg-blue-50 transition duration-300 shadow-md"
              >
                Get in Touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="md:w-1/2">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <Lottie animationData={Team} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <div className="relative">
                <div className="rounded-xl overflow-hidden shadow-xl">
                  <Lottie animationData={Company} />
                </div>
                <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-indigo-100 rounded-full opacity-30 z-0"></div>
                <div className="absolute -top-8 -left-8 w-48 h-48 bg-blue-100 rounded-full opacity-30 z-0"></div>
              </div>
            </div>
            <div className="md:w-1/2">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
                Our Story
              </span>
              <h2 className="text-inter text-2xl md:text-4xl font-bold mb-6 text-gray-900">
                From Vision to Reality
              </h2>
              <p className="text-justify text-gray-600 mb-6">
                Founded in 2015, Techno-Stack-Labs began with a simple mission: to help businesses leverage the power of technology and artificial intelligence to solve complex challenges and drive growth.
              </p>
              <p className="text-justify text-gray-600 mb-6">
                What started as a small team of passionate technologists has grown into a global company serving clients across industries and continents. Throughout our journey, our commitment to innovation, quality, and client success has remained unwavering.
              </p>
              <p className="text-justify text-gray-600 mb-6">
                Today, we are proud to be at the forefront of the digital transformation, helping organizations of all sizes navigate the rapidly evolving technology landscape and harness the power of AI to create lasting value.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">2015</div>
                  <div className="text-gray-600 text-sm">Founded</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">20+</div>
                  <div className="text-gray-600 text-sm">Industries Served</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">100+</div>
                  <div className="text-gray-600 text-sm">Active Clients</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Our Values
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              What Drives Us
            </h2>
            <p className="md:text-lg text-gray-600">
              Our core values guide everything we do, from how we develop solutions to how we interact with our clients and each other.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-6">
                <Rocket size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Innovation</h3>
              <p className="text-gray-600">
                We continuously explore new technologies and approaches to solve complex problems and create breakthrough solutions.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 mb-6">
                <Award size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Excellence</h3>
              <p className="text-gray-600">
                We are committed to delivering the highest quality in everything we do, exceeding expectations and setting new standards.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center text-green-600 mb-6">
                <Heart size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Integrity</h3>
              <p className="text-gray-600">
                We operate with transparency, honesty, and ethical principles, building trust with our clients and within our team.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mb-6">
                <Users size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Collaboration</h3>
              <p className="text-gray-600">
                We believe in the power of teamwork, fostering a collaborative environment where diverse perspectives drive innovation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <StatsSection
        stats={stats}
        sectionStyle="dark"
      />

      {/* Team Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Our Team
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Meet the Experts
            </h2>
            <p className="md:text-lg text-gray-600">
              Our talented team brings together expertise in technology, AI, and industry knowledge to deliver exceptional solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition duration-300">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.imageUrl}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-1 text-gray-900">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-4">{member.position}</p>
                  <p className="text-gray-600 mb-4">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
                Why Choose Us
              </span>
              <h2 className="text-2xl font-inter md:text-4xl font-bold mb-6 text-gray-900">
                What Sets Us Apart
              </h2>
              <p className="text-justify font-inter text-gray-600 mb-6">
                At Techno-Stack-Labs, we differentiate ourselves through our unique approach to technology and client relationships. Here's why leading organizations choose to partner with us:
              </p>

              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="font-inter w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                      1
                    </div>
                  </div>
                  <div>
                    <h3 className="md:text-xl font-bold mb-2 text-gray-900">Technical Excellence</h3>
                    <p className="text-gray-600">
                      Our team comprises top-tier talent with deep expertise in the latest technologies and best practices.
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                      2
                    </div>
                  </div>
                  <div>
                    <h3 className="md:text-xl font-bold mb-2 text-gray-900">Industry Knowledge</h3>
                    <p className="text-gray-600">
                      We combine technical expertise with deep understanding of industry-specific challenges and opportunities.
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                      3
                    </div>
                  </div>
                  <div>
                    <h3 className="md:text-xl font-bold mb-2 text-gray-900">Results-Driven Approach</h3>
                    <p className="text-gray-600">
                      We focus on delivering measurable business outcomes, not just technical solutions.
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                      4
                    </div>
                  </div>
                  <div>
                    <h3 className="md:text-xl font-bold mb-2 text-gray-900">True Partnership</h3>
                    <p className="text-gray-600">
                      We view ourselves as an extension of your team, fully invested in your success.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="grid grid-cols-2 gap-6">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <Lottie animationData={Partner} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg mt-6 mb-6">
                  <Lottie animationData={Office} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg mt-6 mb-6">
                  <Lottie animationData={Knowledge} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <Lottie animationData={ResultDriven} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Our Approach
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              How We Work
            </h2>
            <p className="md:text-lg text-gray-600">
              We follow a collaborative and transparent approach to ensure we deliver solutions that exceed your expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center text-white mb-6">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Discovery</h3>
              <p className="text-gray-600 mb-4">
                We start by understanding your business, challenges, and goals to identify the right technology solution.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <div className="mr-2 text-blue-600">•</div>
                  <span>Business requirements gathering</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 text-blue-600">•</div>
                  <span>Stakeholder interviews</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 text-blue-600">•</div>
                  <span>Technical assessment</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition duration-300">
              <div className="w-16 h-16 bg-indigo-600 rounded-lg flex items-center justify-center text-white mb-6">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Design & Development</h3>
              <p className="text-gray-600 mb-4">
                Our team designs and builds your solution using the latest technologies and best practices.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <div className="mr-2 text-indigo-600">•</div>
                  <span>Solution architecture</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 text-indigo-600">•</div>
                  <span>Agile development</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 text-indigo-600">•</div>
                  <span>Regular progress updates</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition duration-300">
              <div className="w-16 h-16 bg-purple-600 rounded-lg flex items-center justify-center text-white mb-6">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Delivery & Support</h3>
              <p className="text-gray-600 mb-4">
                We ensure smooth implementation and provide ongoing support to help you maximize the value of your solution.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <div className="mr-2 text-purple-600">•</div>
                  <span>Quality assurance</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 text-purple-600">•</div>
                  <span>User training</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 text-purple-600">•</div>
                  <span>Continuous improvement</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CtaSection
        title="Ready to Partner with Us?"
        subtitle="Contact our team to discuss how we can help you achieve your business goals with innovative technology solutions."
        buttonText="Get in Touch"
        buttonLink="/contact"
        sectionStyle="gradient"
      />
    </>
  );
};

export default AboutPage;
